import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Loader2, Check, Camera, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const visitStatusColors = {
  scheduled: 'bg-indigo-500/10 text-indigo-400',
  completed: 'bg-green-500/10 text-green-500',
  cancelled: 'bg-red-500/10 text-red-400',
};

function VisitRow({ visit, headers, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(visit.notes || '');
  const [correction, setCorrection] = useState(visit.correction_notes || '');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const patch = async (payload) => {
    try {
      await axios.patch(`${API}/admin/visits/${visit.id}`, payload, { headers });
      onChanged();
    } catch (err) { console.error(err); }
  };

  const saveNotes = async () => {
    await patch({ notes, correction_notes: correction });
    setEditing(false);
  };

  const remove = async () => {
    if (!window.confirm('Delete this visit?')) return;
    try {
      await axios.delete(`${API}/admin/visits/${visit.id}`, { headers });
      onChanged();
    } catch (err) { console.error(err); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('context', 'visits');
      const res = await axios.post(`${API}/upload`, fd, { headers });
      await patch({ photos: [...(visit.photos || []), res.data.path] });
    } catch (err) { console.error(err); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="border border-white/5 rounded-sm p-4 bg-[#16161a]" data-testid={`visit-row-${visit.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <span className="text-white/80 font-manrope text-sm font-semibold">{visit.date}{visit.time ? ` · ${visit.time}` : ''}</span>
          <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-manrope font-bold ${visitStatusColors[visit.status] || 'bg-white/5 text-white/40'}`} data-testid={`visit-status-${visit.id}`}>
            {visit.status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {visit.status === 'scheduled' && (
            <button onClick={() => patch({ status: 'completed' })} className="flex items-center gap-1.5 text-green-500/70 hover:text-green-500 border border-green-500/20 px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`complete-visit-${visit.id}`}>
              <Check size={11} /> Mark Completed
            </button>
          )}
          {visit.status === 'scheduled' && (
            <button onClick={() => patch({ status: 'cancelled' })} className="text-red-400/60 hover:text-red-400 border border-red-400/20 px-2.5 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`cancel-visit-${visit.id}`}>
              Cancel
            </button>
          )}
          <button onClick={() => setEditing(!editing)} className="text-white/40 hover:text-[#D4AF37] transition-colors" data-testid={`edit-visit-${visit.id}`}><Pencil size={13} /></button>
          <button onClick={remove} className="text-white/20 hover:text-red-400 transition-colors" data-testid={`delete-visit-${visit.id}`}><Trash2 size={13} /></button>
        </div>
      </div>

      {editing ? (
        <div className="space-y-3 mt-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-1 block">Visit Notes</label>
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2 text-white font-manrope text-sm rounded-sm resize-none" data-testid={`visit-notes-input-${visit.id}`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-1 block">Corrections / Follow-ups</label>
            <textarea rows={2} value={correction} onChange={e => setCorrection(e.target.value)} className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2 text-white font-manrope text-sm rounded-sm resize-none" data-testid={`visit-correction-input-${visit.id}`} />
          </div>
          <button onClick={saveNotes} className="bg-[#D4AF37] text-black font-manrope font-bold text-[10px] uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#C5A059] transition-colors" data-testid={`save-visit-notes-${visit.id}`}>
            Save Notes
          </button>
        </div>
      ) : (
        <>
          {visit.notes && <p className="text-white/50 font-manrope text-xs leading-relaxed mt-1">{visit.notes}</p>}
          {visit.correction_notes && (
            <p className="text-yellow-500/70 font-manrope text-xs leading-relaxed mt-1.5 border-l-2 border-yellow-500/40 pl-2">
              Corrections: {visit.correction_notes}
            </p>
          )}
        </>
      )}

      {/* Photos */}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {(visit.photos || []).map((p, i) => (
          <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="block w-14 h-14 rounded-sm overflow-hidden border border-white/10 hover:border-[#D4AF37]/50 transition-colors">
            <img src={`${API}/files/${p}`} alt={`Visit ${i + 1}`} className="w-full h-full object-cover" />
          </a>
        ))}
        <label className="w-14 h-14 rounded-sm border border-dashed border-white/15 hover:border-[#D4AF37]/40 flex items-center justify-center cursor-pointer text-white/30 hover:text-[#D4AF37] transition-colors" data-testid={`add-visit-photo-${visit.id}`}>
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
        </label>
      </div>
    </div>
  );
}

export function VisitsModal({ sub, headers, onClose }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: '', time: '', notes: '' });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/subscriptions/${sub.id}/visits`, { headers });
      setVisits(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub.id]);

  useEffect(() => { load(); }, [load]);

  const addVisit = async (e) => {
    e.preventDefault();
    if (!form.date) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/subscriptions/${sub.id}/visits`, form, { headers });
      setForm({ date: '', time: '', notes: '' });
      load();
    } catch (err) { console.error(err); } finally { setAdding(false); }
  };

  const total = sub.visits_per_year || 0;
  const completed = visits.filter(v => v.status === 'completed').length;
  const remaining = total ? Math.max(0, total - completed) : null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" data-testid="visits-modal">
      <div className="bg-[#111113] border border-[#D4AF37]/30 rounded-sm max-w-2xl w-full p-7 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid="close-visits-modal"><X size={18} /></button>
        <h3 className="font-playfair text-xl text-white mb-1">Service Visits</h3>
        <p className="text-[#D4AF37] font-manrope text-xs uppercase tracking-widest mb-4">{sub.package_name} — {sub.client_name || sub.client_email}</p>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="visits-summary">
          <div className="bg-[#16161a] border border-white/5 rounded-sm p-4 text-center">
            <p className="text-white/30 font-manrope text-[10px] uppercase tracking-widest mb-1">Included / Year</p>
            <p className="font-playfair text-2xl text-white">{total || '—'}</p>
          </div>
          <div className="bg-[#16161a] border border-white/5 rounded-sm p-4 text-center">
            <p className="text-white/30 font-manrope text-[10px] uppercase tracking-widest mb-1">Completed</p>
            <p className="font-playfair text-2xl text-green-500">{completed}</p>
          </div>
          <div className="bg-[#16161a] border border-white/5 rounded-sm p-4 text-center">
            <p className="text-white/30 font-manrope text-[10px] uppercase tracking-widest mb-1">Remaining</p>
            <p className="font-playfair text-2xl text-[#D4AF37]">{remaining ?? '—'}</p>
          </div>
        </div>

        {/* Add visit */}
        <form onSubmit={addVisit} className="flex flex-wrap items-end gap-3 mb-6 bg-[#16161a] border border-white/5 rounded-sm p-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-1 block">Date</label>
            <input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2 text-white font-manrope text-sm rounded-sm" data-testid="new-visit-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-1 block">Time</label>
            <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2 text-white font-manrope text-sm rounded-sm" data-testid="new-visit-time" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-1 block">Notes</label>
            <input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional" className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2 text-white font-manrope text-sm rounded-sm" data-testid="new-visit-notes" />
          </div>
          <button type="submit" disabled={adding} className="flex items-center gap-1.5 bg-[#D4AF37] text-black font-manrope font-bold text-[10px] uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#C5A059] transition-colors disabled:opacity-50" data-testid="add-visit-btn">
            {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Schedule Visit
          </button>
        </form>

        {/* Visit list */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 size={20} className="text-[#D4AF37] animate-spin" /></div>
        ) : visits.length === 0 ? (
          <p className="text-white/30 font-manrope text-sm text-center py-6" data-testid="no-visits">No visits scheduled yet</p>
        ) : (
          <div className="space-y-3">
            {visits.map(v => <VisitRow key={v.id} visit={v} headers={headers} onChanged={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}
